// src/components/common/WeekdaySelector.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import './WeekdaySelector.css';

/**
 * Component for selecting days of the week for recurring posts
 * Uses ISO weekday numbering: 1=Monday, 7=Sunday
 *
 * @param {Object} props
 * @param {Array<number>} props.selectedDays - Array of selected weekday numbers (1-7)
 * @param {function} props.onChange - Callback when selection changes
 * @param {boolean} props.disabled - Whether the selector is disabled
 */
const WeekdaySelector = ({ selectedDays = [], onChange, disabled = false }) => {
  const { t } = useTranslation('common');

  // Weekdays with ISO numbering (1=Monday, 7=Sunday)
  const weekdays = [
    { value: 1, labelKey: 'weekdays.monday', shortKey: 'weekdays.short_monday' },
    { value: 2, labelKey: 'weekdays.tuesday', shortKey: 'weekdays.short_tuesday' },
    { value: 3, labelKey: 'weekdays.wednesday', shortKey: 'weekdays.short_wednesday' },
    { value: 4, labelKey: 'weekdays.thursday', shortKey: 'weekdays.short_thursday' },
    { value: 5, labelKey: 'weekdays.friday', shortKey: 'weekdays.short_friday' },
    { value: 6, labelKey: 'weekdays.saturday', shortKey: 'weekdays.short_saturday' },
    { value: 7, labelKey: 'weekdays.sunday', shortKey: 'weekdays.short_sunday' },
  ];

  const toggleDay = (dayValue) => {
    if (disabled) return;

    const newDays = selectedDays.includes(dayValue)
      ? selectedDays.filter(d => d !== dayValue)
      : [...selectedDays, dayValue].sort((a, b) => a - b);

    onChange(newDays);
  };

  const isSelected = (dayValue) => selectedDays.includes(dayValue);

  // Quick selection helpers
  const selectWeekdays = () => {
    if (disabled) return;
    onChange([1, 2, 3, 4, 5]); // Mon-Fri
  };

  const selectWeekend = () => {
    if (disabled) return;
    onChange([6, 7]); // Sat-Sun
  };

  const selectAll = () => {
    if (disabled) return;
    onChange([1, 2, 3, 4, 5, 6, 7]); // All days
  };

  const selectNone = () => {
    if (disabled) return;
    onChange([]); // Clear all
  };

  return (
    <div className={`wds-container ${disabled ? 'wds-disabled' : ''}`}>
      {/* Quick Selection Buttons */}
      <div className="wds-quick-buttons">
        <button
          type="button"
          className="wds-quick-btn"
          onClick={selectWeekdays}
          disabled={disabled}
          title={t('recurring.select_weekdays')}
        >
          {t('recurring.weekdays')}
        </button>
        <button
          type="button"
          className="wds-quick-btn"
          onClick={selectWeekend}
          disabled={disabled}
          title={t('recurring.select_weekend')}
        >
          {t('recurring.weekend')}
        </button>
        <button
          type="button"
          className="wds-quick-btn"
          onClick={selectAll}
          disabled={disabled}
          title={t('recurring.select_all_days')}
        >
          {t('recurring.every_day')}
        </button>
        <button
          type="button"
          className="wds-quick-btn wds-clear-btn"
          onClick={selectNone}
          disabled={disabled}
          title={t('recurring.clear_selection')}
        >
          {t('buttons.clear')}
        </button>
      </div>

      {/* Weekday Buttons */}
      <div className="wds-days-grid">
        {weekdays.map((day) => (
          <button
            key={day.value}
            type="button"
            className={`wds-day-btn ${isSelected(day.value) ? 'wds-selected' : ''}`}
            onClick={() => toggleDay(day.value)}
            disabled={disabled}
            title={t(day.labelKey)}
          >
            {/* Full name for desktop */}
            <span className="wds-day-full">{t(day.labelKey)}</span>

            {/* Short name for mobile */}
            <span className="wds-day-short">{t(day.shortKey)}</span>

            {/* Check icon when selected */}
            {isSelected(day.value) && (
              <FaCheck className="wds-check-icon" />
            )}
          </button>
        ))}
      </div>

      {/* Selection Summary */}
      {selectedDays.length > 0 && (
        <div className="wds-summary">
          <span className="wds-summary-label">{t('recurring.selected')}:</span>
          <span className="wds-summary-value">
            {selectedDays.length === 7
              ? t('recurring.every_day')
              : selectedDays.length === 5 && selectedDays.every(d => d >= 1 && d <= 5)
              ? t('recurring.weekdays')
              : selectedDays.length === 2 && selectedDays.includes(6) && selectedDays.includes(7)
              ? t('recurring.weekend')
              : selectedDays.map(d => {
                  const day = weekdays.find(wd => wd.value === d);
                  return t(day.labelKey);
                }).join(', ')}
          </span>
        </div>
      )}
    </div>
  );
};

export default WeekdaySelector;
